import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Table,
  Input,
  Popconfirm,
  Spin,
  Flex,
  Form,
  Modal,
  message,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import LocationSelector from "../../components/LocationSelector";
import {
  useFetchCleaningItemsQuery,
  useCreateCleaningItemMutation,
  useUpdateCleaningItemMutation,
  useDeleteCleaningItemMutation,
} from "./cleaning.service";
import "../incident/style.css";

export default function CleaningItemsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.app.user);
  const [locationId, setLocationId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    setLocationId(user?.activeLocationId || user?.restaurantId);
  }, [user]);

  const canManage =
    user?.role === "admin" || user?.role === "local_admin";

  const { data = { data: { items: [] } }, isLoading, refetch } =
    useFetchCleaningItemsQuery(
      { locationId, includeInactive: true },
      { skip: !locationId || !canManage, refetchOnMountOrArgChange: true }
    );

  const items = data?.data?.items || [];
  const [createItem, { isLoading: creating }] = useCreateCleaningItemMutation();
  const [updateItem, { isLoading: updating }] = useUpdateCleaningItemMutation();
  const [deleteItem] = useDeleteCleaningItemMutation();

  if (!canManage) {
    return (
      <Card>
        <p>{t("You do not have permission to manage cleaning items.")}</p>
        <Button onClick={() => navigate("/cleaning")}>{t("BACK")}</Button>
      </Card>
    );
  }

  const handleAdd = async (values) => {
    await createItem({ name: values.name, locationId }).unwrap();
    setAddOpen(false);
    form.resetFields();
    refetch();
  };

  const handleEdit = async (values) => {
    await updateItem({
      id: editRecord._id,
      data: { name: values.name },
      locationId,
    }).unwrap();
    setEditRecord(null);
    form.resetFields();
    refetch();
  };

  const handleDelete = async (id) => {
    await deleteItem({ id, locationId }).unwrap();
    refetch();
  };

  return (
    <Spin spinning={isLoading}>
      <Card
        className="incident-page"
        bordered={false}
        title={
          <Flex align="center" gap="middle" wrap>
            <span>{t("Cleaning items")}</span>
            <LocationSelector onLocationChange={setLocationId} />
          </Flex>
        }
        extra={
          <Flex gap="small">
            <Button onClick={() => navigate("/cleaning")}>{t("BACK")}</Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setAddOpen(true)}
              disabled={!locationId}
            >
              {t("Add item")}
            </Button>
          </Flex>
        }
      >
        <Table
          dataSource={items.filter((i) => i.isActive !== false)}
          rowKey="_id"
          pagination={{ pageSize: 15 }}
        >
          <Table.Column title={t("Item name")} dataIndex="name" />
          <Table.Column
            title={t("TOOLS")}
            render={(_, record) => (
              <Flex gap="small" justify="center">
                <EditOutlined
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setEditRecord(record);
                    form.setFieldsValue({ name: record.name });
                  }}
                />
                <Popconfirm
                  title={t("Remove item")}
                  description={t("Remove this item from the dropdown?")}
                  onConfirm={() => handleDelete(record._id)}
                >
                  <DeleteOutlined style={{ color: "red", cursor: "pointer" }} />
                </Popconfirm>
              </Flex>
            )}
          />
        </Table>
      </Card>

      <Modal
        title={t("Add cleaning item")}
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleAdd}>
          <Form.Item
            name="name"
            label={t("Item name")}
            rules={[{ required: true, message: t("Name is required") }]}
          >
            <Input />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={creating} block>
            {t("Save")}
          </Button>
        </Form>
      </Modal>

      <Modal
        title={t("Edit cleaning item")}
        open={!!editRecord}
        onCancel={() => setEditRecord(null)}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleEdit}>
          <Form.Item
            name="name"
            label={t("Item name")}
            rules={[{ required: true, message: t("Name is required") }]}
          >
            <Input />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={updating} block>
            {t("Save")}
          </Button>
        </Form>
      </Modal>
    </Spin>
  );
}
